import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Pencil, Play, Plus, Trash2, Webhook } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';
import { queryKeys } from '@/queries/keys';
import webhooksService, { WebhookConfig } from '@/lib/api/webhooks';
import WebhookFormDialog from '@/components/integrations/WebhookFormDialog';

const TEST_PAYLOAD_PREVIEW = JSON.stringify(
  {
    event: 'webhook.test',
    timestamp: '2026-01-01T00:00:00Z',
    session: {
      id: '00000000-0000-0000-0000-000000000000',
      email: 'test@example.com',
      session_url: 'https://app.hihobbes.com/analytics/sessions?sessionId=test',
      start_time: '2026-01-01T00:00:00Z',
      end_time: '2026-01-01T00:02:00Z',
      duration_seconds: 120,
    },
    sales_summary: {
      primary_intent: 'This is a test webhook delivery',
      qualification_status: 'qualified',
      booked_status: 'not_booked',
      session_status: 'active',
      buying_intent: 'high',
      engagement_level: 'engaged',
      key_topics: ['webhook integration'],
      next_steps: ['Verify webhook integration'],
      confidence_score: 1.0,
    },
  },
  null,
  2,
);

const WebhooksPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.id;
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<WebhookConfig | undefined>();
  const [testingWebhook, setTestingWebhook] = useState<WebhookConfig | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState<WebhookConfig | null>(null);

  const webhooksQuery = useQuery({
    queryKey: organizationId ? queryKeys.webhooks.list(organizationId) : ['webhooks', 'list', 'no-org'],
    enabled: !!organizationId,
    queryFn: async () => {
      const response = await webhooksService.listWebhooks(organizationId!);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load webhooks.');
      }
      return response.data.webhooks;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const response = await webhooksService.updateWebhook(id, { enabled });
      if (!response.success) {
        throw new Error(response.error || 'Failed to update webhook.');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.list(organizationId!) });
    },
    onError: (error) => {
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await webhooksService.deleteWebhook(id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete webhook.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.list(organizationId!) });
      toast({ title: 'Webhook deleted' });
    },
    onError: (error) => {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await webhooksService.testWebhook(id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Test request failed.');
      }
      return response.data;
    },
    onSuccess: (data) => {
      setTestingWebhook(null);
      if (data.success) {
        toast({ title: 'Test successful', description: data.message || 'Webhook delivered successfully.' });
      } else {
        toast({
          title: 'Test failed',
          description: data.message || `Status ${data.status_code}`,
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      setTestingWebhook(null);
      toast({
        title: 'Test failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (webhook: WebhookConfig) => {
    setEditingWebhook(webhook);
    setIsFormOpen(true);
  };

  const handleDelete = (webhook: WebhookConfig) => {
    setDeletingWebhook(webhook);
  };

  const confirmDelete = () => {
    if (deletingWebhook) {
      deleteMutation.mutate(deletingWebhook.id);
      setDeletingWebhook(null);
    }
  };

  const handleAdd = () => {
    setEditingWebhook(undefined);
    setIsFormOpen(true);
  };

  const webhooks = webhooksQuery.data ?? [];

  const filterBadges = (wh: WebhookConfig) => {
    const badges: string[] = [];
    if (wh.notify_on_qualified) badges.push('Qualified');
    if (wh.notify_on_booked) badges.push('Booked');
    if (wh.notify_on_active_session) badges.push('Active');
    return badges;
  };

  return (
    <>
      <Helmet>
        <title>Webhooks | Integrations | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader
          breadcrumbs={[
            { label: 'Integrations', href: '/integrations' },
            { label: 'Webhooks', icon: Webhook },
          ]}
          actions={
            <Button onClick={handleAdd} size="sm" className="gap-2">
              <Plus className="h-4 w-4" /> Add Webhook
            </Button>
          }
        />

        <div className="flex-1">
          <div className="px-4 sm:px-6 md:px-8 pb-8 pt-2">
            {webhooksQuery.isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading webhooks...
              </div>
            ) : webhooksQuery.isError ? (
              <div className="flex items-center justify-center py-16 text-sm text-destructive">
                Failed to load webhooks. Please refresh the page.
              </div>
            ) : webhooks.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-16 text-sm text-muted-foreground">
                <Webhook className="h-8 w-8 mb-3 opacity-40" />
                <p>No webhooks configured yet.</p>
                <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={handleAdd}>
                  <Plus className="h-4 w-4" /> Create your first webhook
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Filters</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhooks.map((wh) => (
                      <TableRow key={wh.id}>
                        <TableCell className="font-medium">{wh.name || '—'}</TableCell>
                        <TableCell className="max-w-[240px] truncate font-mono text-xs">{wh.url}</TableCell>
                        <TableCell>
                          <Switch
                            checked={wh.enabled}
                            onCheckedChange={() =>
                              toggleMutation.mutate({ id: wh.id, enabled: !wh.enabled })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {filterBadges(wh).length > 0 ? (
                              filterBadges(wh).map((badge) => (
                                <Badge key={badge} variant="secondary" className="text-xs">
                                  {badge}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">None</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(wh)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setTestingWebhook(wh)}
                              title="Test"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(wh)}
                              disabled={deleteMutation.isPending}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>

      {organizationId && (
        <WebhookFormDialog
          open={isFormOpen}
          onOpenChange={(open) => {
            setIsFormOpen(open);
            if (!open) setEditingWebhook(undefined);
          }}
          organizationId={organizationId}
          webhook={editingWebhook}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingWebhook} onOpenChange={(open) => { if (!open) setDeletingWebhook(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingWebhook?.name
                ? `Delete "${deletingWebhook.name}"? This cannot be undone.`
                : 'Delete this webhook? This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test Webhook Dialog */}
      <Dialog open={!!testingWebhook} onOpenChange={(open) => { if (!open) setTestingWebhook(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Test Webhook</DialogTitle>
            <DialogDescription>
              Send a test payload to verify your webhook endpoint is reachable.
            </DialogDescription>
          </DialogHeader>
          {testingWebhook && (
            <div className="space-y-3 overflow-hidden">
              <div className="space-y-1 text-sm">
                <div><span className="text-muted-foreground">Name:</span> {testingWebhook.name || '—'}</div>
                <div><span className="text-muted-foreground">URL:</span> <span className="font-mono text-xs break-all">{testingWebhook.url}</span></div>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium">Payload</span>
                <pre className="rounded-md bg-muted p-3 text-xs font-mono overflow-auto max-h-64">
                  {TEST_PAYLOAD_PREVIEW}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestingWebhook(null)} disabled={testMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => testingWebhook && testMutation.mutate(testingWebhook.id)}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                </span>
              ) : (
                'Send Test'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WebhooksPage;
